import { AfterViewInit, Component, inject, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, ReplaySubject, finalize, map, of, takeUntil, tap } from 'rxjs';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { Router } from '@angular/router';
import { UsersService } from 'core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { AuthService } from 'core/services/auth.service';
import { User } from '../../../../shared/types/user.model';
import { KorviuTextareaComponent } from '../../../../shared/components/korviu-textarea/korviu-textarea.component';
import { CustomMessageService } from 'core/services/custom-message.service';
import { KorviuLoaderComponent } from '../../../../shared/components/korviu-loader/korviu-loader.component';
import { KorviuInputComponent } from '../../../../shared/components/korviu-input/korviu-input.component';
import { KorviuButtonComponent } from '../../../../shared/components/korviu-button/korviu-button.component';

import { FileSizePipe } from '../../../../shared/pipes/file-size.pipe';

@Component({
  selector: 'app-contact-us-modal',
  templateUrl: './contact-us-modal.component.html',
  styleUrl: './contact-us-modal.component.scss',
  standalone: true,
  imports: [
    KorviuLoaderComponent,
    TranslateModule,
    ReactiveFormsModule,
    KorviuInputComponent,
    KorviuTextareaComponent,
    NzUploadModule,
    KorviuButtonComponent,
    FileSizePipe,
    NzModalModule
  ]
})
export class ContactUsModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('questionField') public questionField: KorviuTextareaComponent;

  readonly #modalData = inject<{ initialQuestion?: string } | null>(NZ_MODAL_DATA, { optional: true });

  public isLoading: boolean;
  public contactUsForm: FormGroup;
  public fileList: NzUploadFile[] = [];
  private readonly _onDestroy$: ReplaySubject<void>;

  public get balance(): boolean {
    return this._router.url.startsWith('/workspace/balance');
  }

  public get faq(): boolean {
    return this._router.url.startsWith('/workspace/faq');
  }

  public get firstNameControl(): AbstractControl {
    return this.contactUsForm?.get('first_name');
  }
  public get lastNameControl(): AbstractControl {
    return this.contactUsForm?.get('last_name');
  }

  public get phoneNumberControl(): AbstractControl {
    return this.contactUsForm?.get('phone_number');
  }

  public get phoneNumberCodeControl(): AbstractControl {
    return this.contactUsForm?.get('phone_number_code');
  }

  public get emailControl(): AbstractControl {
    return this.contactUsForm?.get('email');
  }

  public get questionControl(): AbstractControl {
    return this.contactUsForm?.get('questions');
  }

  constructor(
    private _usersService: UsersService,
    private _translateService: TranslateService,
    private _messageService: CustomMessageService,
    private _modal: NzModalRef,
    private _router: Router,
    private _authService: AuthService,
    private _renderer: Renderer2
  ) {
    this.contactUsForm = new FormGroup({
      first_name: new FormControl('', [Validators.required, Validators.pattern(/^[\p{L} ]+$/u)]),
      last_name: new FormControl('', [Validators.required, Validators.pattern(/^[\p{L} ]+$/u)]),
      phone_number_code: new FormControl('', [Validators.required]),
      phone_number: new FormControl('', [
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        Validators.minLength(10),
        Validators.maxLength(10)
      ]),
      email: new FormControl('', [Validators.required, Validators.email]),
      questions: new FormControl('', [Validators.required]),
      files: new FormControl([])
    });
    this.isLoading = false;
    this._onDestroy$ = new ReplaySubject<void>(1);
  }

  public ngOnInit(): void {
    const user: User = this._authService?.currentUser;
    const phonePrefix = user?.phoneNumberCountryCode || '';
    const fullPhoneNumber = user?.fullPhoneNumber || '';
    this.contactUsForm.patchValue({
      first_name: user?.firstName,
      last_name: user?.lastName,
      phone_number:
        phonePrefix && fullPhoneNumber.startsWith(phonePrefix)
          ? fullPhoneNumber.slice(phonePrefix.length)
          : fullPhoneNumber,
      email: user?.email,
      phone_number_code: phonePrefix,
      questions: this.#modalData?.initialQuestion ?? ''
    });
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this._renderer.selectRootElement(this.questionField?.textAreaElement?.nativeElement).focus();
    }, 300);
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public onCancelClick(): void {
    this._modal.close(false);
  }

  public onSubmitContactForm(): void {
    const formData: FormData = new FormData();
    Object.keys(this.contactUsForm.controls).forEach((key: string) => {
      if (key !== 'files') {
        formData.append(key, this.contactUsForm.controls[key].value);
      }
    });

    this.fileList.forEach((fileItem: NzUploadFile) => {
      formData.append('files', fileItem as any);
    });

    this.isLoading = true;
    this._usersService
      .submitContactForm(formData)
      .pipe(
        tap(() => {
          this._translateService.get('common.contactFormSuccessful').subscribe((data: string) => {
            this._messageService.success(data);
          });
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe(() => {
        this._modal.close(true);
      });
  }

  public beforeUpload = (file: NzUploadFile): Observable<boolean> => {
    const isValidType: boolean = file.type === 'image/jpeg' || file.type === 'image/png';
    const isValidSize: boolean = file.size / 1024 / 1024 < 5;
    const isWithinLimit: boolean = this.fileList.length < 3;

    if (!isValidType) {
      this._messageService.error(this._translateService.instant('common.invalidFileType'));
      return of(false);
    }

    if (!isValidSize) {
      this._messageService.error(this._translateService.instant('common.fileSizeExceeded'));
      return of(false);
    }

    if (!isWithinLimit) {
      this._messageService.error(this._translateService.instant('common.fileLimitExceeded'));
      return of(false);
    }

    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      file.thumbUrl = e.target.result;
      this.fileList = [...this.fileList, file];
      this.contactUsForm.patchValue({ files: this.fileList });
    };
    reader.readAsDataURL(file as any);

    return of(false);
  };

  public removeFile = (file: NzUploadFile): boolean => {
    this.fileList = this.fileList.filter(item => item.uid !== file.uid);
    this.contactUsForm.patchValue({ files: this.fileList });
    return true;
  };
}
